import { mapState } from 'vuex';
import {
  EntrancePlantDetailDTO,
  GateGuestDTO,
  TimeSlot,
} from '@/../client-generator/generated-clients/plants';
import { PropType } from 'vue';
import { Options, Vue } from 'vue-class-component';
import plantsClient from '@/core/services/plants';
import { SET_APP_LOADING } from '@/core/store/action-types';
import ContactTab from './ContactTab/ContactTab.vue';
import CodeTab from './CodeTab/CodeTab.vue';
import GateAccessTab from './GateAccessTab/GateAccessTab.vue';
import BadgeTab from './BadgeTab/BadgeTab.vue';
import DialogConfirmDelete from '@/views/components/DialogConfirmDelete/DialogConfirmDelete.vue';

export interface TimeSlotWithValidation {
  slot: TimeSlot;
  isValid: boolean;
}

@Options({
  name: 'GuestDetailsEdit',
  props: {
    guestSelected: {
      type: Object as PropType<GateGuestDTO>,
      required: true,
    },
  },
  computed: {
    ...mapState(['currentEntrancePlant']),
  },
  components: {
    ContactTab,
    GateAccessTab,
    BadgeTab,
    CodeTab,
    DialogConfirmDelete,
  },
  emits: ['delete-guest', 'update-list'],
})
export default class GuestDetailsEdit extends Vue {
  currentEntrancePlant!: EntrancePlantDetailDTO;
  guestSelected!: GateGuestDTO;
  editedGuest: GateGuestDTO = new GateGuestDTO();

  timesSlots: TimeSlotWithValidation[] = [];
  guestContactEdited = false;
  guestGateAccessEdited = false;
  guestBadgeEdited = false;
  guestKeyCodeEdited = false;
  errorContact = false;
  errorGateAccess = false;
  errorBadge = false;
  errorKeyCode = false;
  isVisibleDeleteGuestConfirm = false;
  updateGuesttKey: number = 0;

  created() {
    this.initGuestToEdit();
  }

  get guestWasEdited() {
    const tabEdited =
      this.guestContactEdited ||
      this.guestGateAccessEdited ||
      this.guestBadgeEdited ||
      this.guestKeyCodeEdited;

    const tabHasError =
      this.errorContact ||
      this.errorGateAccess ||
      this.errorBadge ||
      this.errorKeyCode;

    return tabEdited && !tabHasError;
  }

  get gates() {
    return this.currentEntrancePlant.gates;
  }

  initGuestToEdit() {
    this.timesSlots = [];

    this.editedGuest = new GateGuestDTO(this.guestSelected);
    this.editedGuest.gateIds = this.guestSelected.gateIds?.map((id) => id) ?? [];
    this.editedGuest.repetitionDays = this.guestSelected.repetitionDays?.map(
      (day) => day
    );
    if (this.editedGuest.timesSlots) {
      this.editedGuest.timesSlots.every((slot) =>
        this.timesSlots.push({
          slot: new TimeSlot({
            id: slot.id,
            timeSince: new Date(slot.timeSince!),
            timeUntil: new Date(slot.timeUntil!),
          }),
          isValid: true,
        })
      );
    }
  }

  toggleDeleteGuestConfirm() {
    this.isVisibleDeleteGuestConfirm = !this.isVisibleDeleteGuestConfirm;
  }

  handleDeleteGuest() {
    this.$emit('delete-guest', this.guestSelected.id);
  }

  handleContactEdited(value: boolean) {
    this.guestContactEdited = value;
  }

  handleGateAccesEdited(value: boolean) {
    this.guestGateAccessEdited = value;
  }

  handleBadgeEdited(value: boolean) {
    this.guestBadgeEdited = value;
  }

  handleKeyCodeEdited(value: boolean, keyCode: string) {
    this.guestKeyCodeEdited = value;
    if (!this.errorKeyCode) {
      this.editedGuest.keypadCode = keyCode;
    }
  }

  handleErrorContact(value: boolean) {
    this.errorContact = value;
  }

  handleErrorGateAccess(value: boolean) {
    this.errorGateAccess = value;
  }

  handleErrorBadge(value: boolean) {
    this.errorBadge = value;
  }

  handleErrorKeyCode(value: boolean) {
    this.errorKeyCode = value;
  }

  retrieveEditedTimesSlots() {
    const retrievedTimesSlots: TimeSlot[] = [];
    this.timesSlots.every((editedTime) =>
      retrievedTimesSlots.push(editedTime.slot)
    );
    return retrievedTimesSlots;
  }

  async handleSaveEditedGuest() {
    this.editedGuest.timesSlots = this.retrieveEditedTimesSlots();

    this.$store.dispatch(SET_APP_LOADING, true);
    try {
      await plantsClient.guestPUT(
        this.currentEntrancePlant.desktopId as number,
        this.editedGuest
      );
      this.replaceGuest();
      this.restoreEditCheckers();
      this.$toast.add({
        severity: 'success',
        summary: this.$t('entrancePlant.gateGuestBannerUpdated'),
        closable: false,
        life: 3000,
      });

      this.$emit('update-list');
    } catch {
      this.$toast.add({
        severity: 'error',
        summary: this.$t('general.operationError'),
        closable: true,
        life: 3000,
      });
    } finally {
      this.forceUpdateGuestDetails();
      this.$store.dispatch(SET_APP_LOADING, false);
    }
  }

  replaceGuest() {
    const index = this.currentEntrancePlant.gateGuests!.findIndex(
      (guest) => guest.id === this.editedGuest.id
    );
    if (index !== -1) {
      this.currentEntrancePlant.gateGuests!.splice(index, 1, this.editedGuest);
    }
  }

  restoreEditCheckers() {
    this.guestContactEdited = false;
    this.guestGateAccessEdited = false;
    this.guestBadgeEdited = false;
    this.guestKeyCodeEdited = false;
    this.errorContact = false;
    this.errorGateAccess = false;
    this.errorBadge = false;
    this.errorKeyCode = false;
  }

  forceUpdateGuestDetails() {
    this.updateGuesttKey += 1;
  }
}
